import { FC, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  appBarActionsId,
  appBarTitleId,
  appBarStickyHeaderId,
  appBarMenuId,
} from 'packages/main/components/Layouts/PrivateLayout';
import DefaultMenuLayout from 'packages/main/components/Layouts/DefaultMenuLayout';
import { Space } from 'antd';
import { Helmet } from 'react-helmet';

export interface TitlePortalProps {
  text: string;
  children?: React.ReactNode;
}

function waitForElement(
  selector: string,
  callback: (el: HTMLDivElement) => void
) {
  const intervalId = setInterval(() => {
    const el = document.querySelector<HTMLDivElement>(`#${selector}`);
    if (el) {
      clearInterval(intervalId);
      callback(el);
    }
  }, 500);
}

/**
 *
 * @param props.text string Required text to be used as the title of the page
 * @param props.children React.ReactNode Used in place of text for the actual html
 */
export const TitlePortal = ({
  text,
  children,
}: TitlePortalProps): JSX.Element | null => {
  const [titleContainer, setTitleContainer] = useState<HTMLDivElement | null>(
    null
  );
  useEffect(() => {
    if (!titleContainer) {
      waitForElement(appBarTitleId, (el) => {
        setTitleContainer(el);
      });
    }
  }, [titleContainer]);
  if (!titleContainer) {
    return null;
  }
  return createPortal(
    <>
      <Helmet>
        <title>{`${text} - Machine Learning Frontend`}</title>
      </Helmet>
      {children ? children : text}
    </>,
    titleContainer,
    'app-title-portal'
  );
};

export const AppBarActionsPortal: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const appBarActionsContainer = document.getElementById(appBarActionsId);

  return appBarActionsContainer
    ? createPortal(<Space>{children}</Space>, appBarActionsContainer)
    : null;
};

export const ProfileMenuPortal: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [menuContainer, setMenuContainer] = useState<HTMLElement | null>(null);

  // Bug Fix: the first time ProfileMenuPortal is invoked, the menuContainer is not yet ready
  useLayoutEffect(() => {
    const checkMenuContainer = () => {
      const container = document.getElementById(appBarMenuId);
      if (container) {
        setMenuContainer(container);
      } else {
        requestAnimationFrame(checkMenuContainer);
      }
    };

    checkMenuContainer();
  }, []);

  return menuContainer
    ? createPortal(children ? children : <DefaultMenuLayout />, menuContainer)
    : null;
};

export const StickyHeaderPortal: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const appBarStickyHeaderContainer =
    document.getElementById(appBarStickyHeaderId);

  return appBarStickyHeaderContainer
    ? createPortal(children, appBarStickyHeaderContainer)
    : null;
};
